import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle, Smile, Sliders } from "react-feather";
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  dismissActionItemAPI,
  getActionCenterItemsAPI,
  resolveActionItemAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./ActionCenter.module.scss";

const PRIORITY_BADGE = {
  critical: "danger",
  high: "warning",
  medium: "info",
  low: "secondary",
};

const PRIORITY_COLOR = {
  critical: "rgba(247,49,100,0.12)",
  high: "rgba(255,163,25,0.12)",
  medium: "rgba(100,73,152,0.10)",
  low: "rgba(82,82,108,0.08)",
};

export default function ActionCenter() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [urgency, setUrgency] = useState("All");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getActionCenterItemsAPI();
      setRows(res.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (urgency !== "All" && r.priority !== urgency.toLowerCase())
        return false;
      if (moduleFilter !== "All" && r.module !== moduleFilter) return false;
      return true;
    });
  }, [rows, urgency, moduleFilter]);

  useEffect(() => {
    if (selected && !filtered.find((x) => x.id === selected.id))
      setSelected(null);
  }, [filtered, selected]);

  const openResolve = () => {
    setNote("");
    setNoteOpen(true);
  };

  const confirmResolve = async () => {
    if (!selected) return;
    try {
      await resolveActionItemAPI(selected.id, note);
      toast.success("Marked resolved");
      setNoteOpen(false);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Priority",
        width: "110px",
        cell: (r) => (
          <Badge
            bg={PRIORITY_BADGE[r.priority] || "secondary"}
            style={{
              background: PRIORITY_COLOR[r.priority],
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: 999,
            }}
          >
            {r.priority}
          </Badge>
        ),
      },
      { name: "Item", selector: (r) => r.item, grow: 2 },
      { name: "Module", selector: (r) => r.module, width: "120px" },
      {
        name: "Due",
        selector: (r) => new Date(r.dueAt).toLocaleString(),
        sortable: true,
        width: "160px",
      },
      { name: "Owner", selector: (r) => r.owner || "—", width: "160px" },
      {
        name: "Action",
        width: "230px",
        cell: (r) => (
          <div className="d-flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="outline-primary"
              style={{
                "--bs-btn-border-color": "var(--theme-default)",
                "--bs-btn-color": "var(--theme-default)",
                "--bs-btn-hover-bg": "var(--theme-default)",
                "--bs-btn-hover-border-color": "var(--theme-default)",
              }}
              onClick={() => navigate(companyUrl(r.deepLink))}
            >
              Resolve
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => toast.info("Assigned (mock)")}
            >
              Assign
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              style={{
                "--bs-btn-border-color": "var(--theme-secondary)",
                "--bs-btn-color": "var(--theme-secondary)",
                "--bs-btn-hover-bg": "var(--theme-secondary)",
                "--bs-btn-hover-border-color": "var(--theme-secondary)",
              }}
              onClick={async () => {
                try {
                  await dismissActionItemAPI(r.id);
                  toast.success("Dismissed");
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Dismiss
            </Button>
          </div>
        ),
      },
    ],
    [load, navigate],
  );

  // ── Loading / Error / Empty guards ──────────────────────────────────────────
  const breadcrumbs = [
    { label: "Company", path: companyUrl("dashboard") },
    { label: "Dashboard", path: companyUrl("dashboard") },
    { label: "Actions" },
  ];

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Action center" breadcrumbs={breadcrumbs} />
        <LoadingSkeleton rows={6} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Action center" breadcrumbs={breadcrumbs} />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Action center"
        breadcrumbs={breadcrumbs}
        secondaryActions={[
          { label: "Refresh", onClick: load, variant: "outline-secondary" },
        ]}
      />

      {/* ── Filter bar ── */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3 gap-md-2">
        <span
          className="d-flex align-items-center gap-1 small me-1"
          style={{
            color: "var(--chart-text-color)",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontSize: "0.7rem",
          }}
        >
          <Sliders size={13} />
          Urgency
        </span>

        <ButtonGroup size="sm" style={{ gap: "12px" }}>
          {["All", "Critical", "High", "Medium", "Low"].map((u) => (
            <Button
              key={u}
              variant={urgency === u ? "primary" : "outline-secondary"}
              className={styles.chip}
              style={{
                ...(urgency === u && {
                  background: "var(--theme-default)",
                  borderColor: "var(--theme-default)",
                }),
              }}
              onClick={() => setUrgency(u)}
            >
              {u}
            </Button>
          ))}
        </ButtonGroup>

        <Form.Select
          size="sm"
          style={{
            width: 200,
            borderColor: "var(--recent-border)",
            color: "var(--body-font-color)",
            background: "var(--white)",
            borderRadius: "0.5rem",
            fontSize: "0.82rem",
          }}
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="ms-md-2"
        >
          {["All", "Meetings", "Integrations", "Users", "MOM"].map((m) => (
            <option key={m} value={m}>
              {m === "All" ? "All modules" : m}
            </option>
          ))}
        </Form.Select>

        {/* Live count pill */}
        {filtered.length > 0 && (
          <span
            className="ms-auto"
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              color: "var(--theme-default)",
              background: "rgba(100,73,152,0.1)",
              borderRadius: 999,
              padding: "3px 12px",
            }}
          >
            {filtered.length} item{filtered.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* ── Table or empty ── */}
      {!loading && filtered.length === 0 ? (
        <EmptyState
          icon={Smile}
          title="No pending actions!"
          description="Everything is running smoothly."
          actionLabel="Back to dashboard"
          onAction={() => navigate(companyUrl("dashboard"))}
        />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={filtered}
          loading={loading}
          showSearch
          keyField="id"
          onRowClick={(r) => setSelected(r)}
        />
      )}

      {/* ── Detail panel ── */}
      <Row className="mt-3">
        <Col lg={8}>
          <div className={styles.detailPanel}>
            <h2 className="h6">Selected item detail</h2>
            {!selected ? (
              <p className="text-muted small mb-0">
                Select a row to see suggested steps.
              </p>
            ) : (
              <>
                {/* Priority ribbon */}
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Badge
                    bg={PRIORITY_BADGE[selected.priority] || "secondary"}
                    style={{
                      fontSize: "0.68rem",
                      borderRadius: 999,
                      padding: "3px 10px",
                    }}
                  >
                    {selected.priority}
                  </Badge>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--chart-text-color)",
                    }}
                  >
                    {selected.module}
                  </span>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--chart-text-color)",
                    }}
                  >
                    · Due {new Date(selected.dueAt).toLocaleDateString()}
                  </span>
                </div>

                <p
                  className="mb-3"
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--body-font-color)",
                    lineHeight: 1.65,
                  }}
                >
                  {selected.suggestedStep}
                </p>

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    as={Link}
                    size="sm"
                    to={companyUrl(selected.deepLink)}
                    style={{
                      background: "var(--theme-default)",
                      borderColor: "var(--theme-default)",
                      fontSize: "0.8rem",
                    }}
                  >
                    Open {selected.module}
                  </Button>
                  <Button
                    size="sm"
                    variant="success"
                    style={{
                      background: "var(--theme-secondary)",
                      borderColor: "var(--theme-secondary)",
                      fontSize: "0.8rem",
                    }}
                    onClick={openResolve}
                  >
                    <CheckCircle size={13} className="me-1" />
                    Mark resolved with note
                  </Button>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* ── Resolve modal ── */}
      <Modal show={noteOpen} onHide={() => setNoteOpen(false)} centered>
        <Modal.Header
          closeButton
          style={{
            borderBottom: "1px solid var(--recent-border)",
            background: "var(--light-bg)",
          }}
        >
          <Modal.Title
            style={{
              fontSize: "0.95rem",
              fontWeight: 700,
              color: "var(--theme-default)",
            }}
          >
            Resolve action
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--white)" }}>
          <Form.Label
            style={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "var(--chart-text-color)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Note (optional)
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{
              borderColor: "var(--recent-border)",
              background: "var(--light-bg)",
              color: "var(--body-font-color)",
              borderRadius: "0.5rem",
              fontSize: "0.85rem",
            }}
          />
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: "1px solid var(--recent-border)",
            background: "var(--light-bg)",
          }}
        >
          <Button
            variant="primary"
            onClick={confirmResolve}
            style={{
              background: "var(--theme-default)",
              borderColor: "var(--theme-default)",
              fontSize: "0.82rem",
              borderRadius: "0.5rem",
            }}
          >
            <CheckCircle size={13} className="me-1" />
            Confirm resolve
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
